import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { X, MoveRight } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import DatePickField from "./DatePickField";
import { shiftIso, wibTodayIso } from "./dateFormat";
import { apiFetch } from "./api";

// Walk-in rate simulator, for BOTH products the property sells.
//
// Menginap: staff pick check-in + check-out; the server sums the nightly rate for every night, so
// day-of-week rates, calendar overrides and dynamic pricing all apply across the stay.
//
// Transit: one date, priced for every duration currently on sale. A day-use stay is a single slot,
// so the interesting axis is hours rather than nights — and dynamic occupancy tiers deliberately do
// not apply to it, which is why the two modes cannot share one table.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const Intro = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Dates = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  height: 2.15rem;
  color: ${theme.muted};
`;

const NightsPill = styled.div`
  align-self: flex-start;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: ${theme.goldSoft};
  border: 1px solid ${theme.gold};
  font-size: 0.78rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid ${theme.border};
  &:last-child {
    border-bottom: 0;
  }
`;

const RtName = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const RtSub = styled.div`
  margin-top: 0.1rem;
  font-size: 0.76rem;
  color: ${theme.muted};
`;

const RtTotal = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
  white-space: nowrap;
`;

const Muted = styled.div`
  padding: 0.5rem 0.25rem;
  font-size: 0.85rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f2c4c0;
  color: #b91c1c;
  font-size: 0.82rem;
  font-weight: 600;
`;

const FootNote = styled.div`
  font-size: 0.74rem;
  line-height: 1.4;
  color: ${theme.muted};
`;

const Modes = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-bottom: 0.7rem;
`;

const ModeBtn = styled.button`
  background: ${(props) => (props.$on ? theme.ink : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.ink : theme.border)};
  border-radius: 999px;
  color: ${(props) => (props.$on ? "#ffffff" : theme.ink700)};
  cursor: pointer;
  flex: 1;
  font-size: 0.82rem;
  font-weight: 800;
  padding: 0.4rem 0.6rem;
`;

const DurCell = styled.div`
  align-items: baseline;
  display: flex;
  gap: 0.3rem;
  justify-content: space-between;
  padding: 0.1rem 0;
`;

const DurLabel = styled.span`
  color: #4a4f63;
  font-size: 0.78rem;
  font-weight: 700;
`;

const DurValue = styled.span`
  color: ${theme.ink};
  font-size: 0.86rem;
  font-weight: 800;
`;

// A price that differs from the room type's normal day-of-week rate for this date.
const OverrideTag = styled.span`
  background: #fdf3e3;
  border-radius: 999px;
  color: #8a5a00;
  font-size: 0.64rem;
  font-weight: 800;
  margin-left: 0.3rem;
  padding: 0.02rem 0.3rem;
`;

const RateSimulationModal = (props) => {
  const { t } = useT();
  const [mode, setMode] = useState("stay");
  const [transitDate, setTransitDate] = useState(() => wibTodayIso());
  const [checkIn, setCheckIn] = useState(() => wibTodayIso());
  const [checkOut, setCheckOut] = useState(() => shiftIso(wibTodayIso(), 1));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const reqRef = useRef(0);

  const isTransit = mode === "transit";
  const rangeValid = isTransit || checkOut > checkIn;

  const changeCheckIn = (value) => {
    setCheckIn(value);
    if (value >= checkOut) {
      setCheckOut(shiftIso(value, 1));
    }
  };

  // Re-fetch whenever a valid range changes. The fetch lives in an inner async fn (not the effect
  // body) so the loading/result state is only set from callbacks. reqRef + `active` guard against
  // out-of-order responses and unmount. An invalid range is handled in render, not via state.
  useEffect(() => {
    if (!rangeValid) {
      return undefined;
    }
    const reqId = reqRef.current + 1;
    reqRef.current = reqId;
    let active = true;

    const run = async () => {
      setLoading(true);
      setErrorKey("");
      try {
        const query = isTransit
          ? `mode=transit&date=${transitDate}`
          : `check_in=${checkIn}&check_out=${checkOut}`;
        const res = await apiFetch(`/frontdesk_v2/rate_simulation?${query}`);
        if (!active || reqId !== reqRef.current) {
          return;
        }
        if (res && res.data) {
          setResult(res.data);
        } else {
          setResult(null);
          setErrorKey("sim_error");
        }
      } catch {
        if (active && reqId === reqRef.current) {
          setResult(null);
          setErrorKey("sim_error");
        }
      } finally {
        if (active && reqId === reqRef.current) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [checkIn, checkOut, transitDate, isTransit, rangeValid]);

  const roomTypes = result ? result.room_types : [];

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>{isTransit ? t("sim_title_transit") : t("sim_title")}</Title>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Content>
          <Modes>
            <ModeBtn
              type="button"
              $on={!isTransit}
              onClick={() => setMode("stay")}
            >
              {t("sim_mode_stay")}
            </ModeBtn>
            <ModeBtn
              type="button"
              $on={isTransit}
              onClick={() => setMode("transit")}
            >
              {t("sim_mode_transit")}
            </ModeBtn>
          </Modes>

          <Intro>{isTransit ? t("sim_intro_transit") : t("sim_intro")}</Intro>

          {isTransit ? (
            <Dates>
              <DatePickField
                label={t("sim_date")}
                value={transitDate}
                onChange={setTransitDate}
              />
            </Dates>
          ) : (
            <Dates>
              <DatePickField
                label={t("sim_check_in")}
                value={checkIn}
                onChange={changeCheckIn}
              />
              <Arrow>
                <MoveRight size={16} />
              </Arrow>
              <DatePickField
                label={t("sim_check_out")}
                value={checkOut}
                min={shiftIso(checkIn, 1)}
                onChange={setCheckOut}
              />
            </Dates>
          )}

          {!rangeValid ? <ErrorNote>{t("sim_range_error")}</ErrorNote> : null}

          {rangeValid && errorKey ? <ErrorNote>{t(errorKey)}</ErrorNote> : null}

          {rangeValid && loading ? <Muted>{t("sim_loading")}</Muted> : null}

          {rangeValid && !loading && result && isTransit ? (
            <>
              <NightsPill>
                {(result.durations || []).join(" · ")} {t("transit_hours")}
              </NightsPill>
              {roomTypes.length === 0 ? (
                <Muted>{t("sim_empty_transit")}</Muted>
              ) : (
                <Table>
                  {roomTypes.map((rt) => (
                    <Row key={rt.code} style={{ display: "block" }}>
                      <RtName>{rt.name}</RtName>
                      {rt.unconfigured ? (
                        <RtSub>{t("sim_unconfigured")}</RtSub>
                      ) : (
                        (rt.rates || []).map((rate) => (
                          <DurCell key={rate.duration_hours}>
                            <DurLabel>
                              {rate.duration_hours} {t("transit_hours")}
                            </DurLabel>
                            <DurValue>
                              {rate.formatted}
                              {rate.override ? (
                                <OverrideTag>{t("sim_override")}</OverrideTag>
                              ) : null}
                            </DurValue>
                          </DurCell>
                        ))
                      )}
                    </Row>
                  ))}
                </Table>
              )}
              <FootNote>{t("sim_note_transit")}</FootNote>
            </>
          ) : null}

          {rangeValid && !loading && result && !isTransit ? (
            <>
              <NightsPill>
                {result.nights} {t("sim_nights")}
              </NightsPill>
              {roomTypes.length === 0 ? (
                <Muted>{t("sim_empty")}</Muted>
              ) : (
                <Table>
                  {roomTypes.map((rt) => (
                    <Row key={rt.code}>
                      <div>
                        <RtName>{rt.name}</RtName>
                        <RtSub>
                          {rt.nights} {t("sim_nights")} · {t("sim_avg")}{" "}
                          {rt.avg_formatted}
                          {rt.incomplete ? ` · ${t("sim_incomplete")}` : ""}
                        </RtSub>
                      </div>
                      <RtTotal>{rt.total_formatted}</RtTotal>
                    </Row>
                  ))}
                </Table>
              )}
              <FootNote>{t("sim_note")}</FootNote>
            </>
          ) : null}
        </Content>
      </Panel>
    </Overlay>
  );
};

export default RateSimulationModal;
